import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import Header from "./components/Header";
import LandingPageWithRefresh from "./components/LandingPageWithRefresh";
import About from "./components/About";
import Changelog, { ChangelogDetail } from "./components/Changelog";
import ChessLandingPage from "./components/chess/ChessLandingPage";
import ChessGamesView from "./components/chess/ChessGamesView";
import ChessReplay from "./components/chess/ChessReplay";
import ChessOffline from "./components/chess/ChessOffline";
import GameChess from "./components/chess/Chess";
import LarvaLegendsLandingPage from "./components/larvalegends/LarvaLegendsLandingPage";
import LarvaLegends from "./components/larvalegends/LarvaLegends";
import MfersBeheadedNewLanding from "./components/mfersbeheaded/MfersBeheadedNewLanding";
import MfersBeheadedGame from "./components/mfersbeheaded/MfersBeheadedGame";
import MfersBeheadedSettingsPage from "./components/mfersbeheaded/MfersBeheadedSettingsPage";
import Leaderboard from "./components/mfersbeheaded/Leaderboard";
import MfersTetrisLandingPage from "./components/mferstetris/MfersTetrisLandingPage";
import MfersTetris from "./components/mferstetris/MfersTetris";
import Alien2048 from "./components/alien2048/Alien2048";
import Alien2048NewLanding from "./components/alien2048/Alien2048NewLanding";
import Alien2048SettingsPage from "./components/alien2048/Alien2048SettingsPage";
import Alien2048Leaderboard from "./components/alien2048/Leaderboard";
import ReplayList from "./components/replay/ReplayList";
import ReplayViewer from "./components/replay/ReplayViewer";
import ArcadeSettings from "./components/ArcadeSettings";
import { TwitterCallback, RegistrationScreen } from "./components/auth";
import { WalletProvider } from "./providers/WalletProvider";
import { AuthProvider } from "./contexts/AuthContext";
import logger from "./utils/logger";
import DebugConsole from "./components/DebugConsole";
import ScrollDebug from "./components/ScrollDebug";
import ErrorBoundary from "./components/ErrorBoundary";
import GameErrorBoundary from "./components/GameErrorBoundary";
import PrivateRoute from "./components/PrivateRoute";
// Bug reports feature disabled
// import AdminBugsPage from "./components/admin/AdminBugsPage";
import BugReportButton from "./components/BugReportButton";
import './App.css';

function App() {
  const [twitterUser, setTwitterUser] = useState(null);
  const [oauthToken, setOauthToken] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Restore user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem('twitterUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setTwitterUser(userData);
        logger.info('Restored user from localStorage', { username: userData.screen_name });
      } catch (error) {
        logger.error('Error parsing saved user data:', error);
        localStorage.removeItem('twitterUser');
      }
    }
  }, []);

  useEffect(() => {
    logger.info('MfersArcade App initialized', {
      url: window.location.href,
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Log current logger status
    const status = logger.getStatus();
    if (status.enhancedLogging) {
      logger.info('Enhanced logging enabled', status);
    }

    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const oauthVerifier = urlParams.get('oauth_verifier');
    const oauthTokenParam = urlParams.get('oauth_token');

    if (oauthVerifier && oauthTokenParam) {
      const getUserIdentity = async () => {
        try {
          // Retrieve state token from sessionStorage (if it exists)
          const stateToken = sessionStorage.getItem('oauth_state');

          const response = await fetch("/api/auth/twitter/access-token", {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              oauth_verifier: oauthVerifier,
              oauth_token: oauthTokenParam,
              state: stateToken
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to verify OAuth token');
          }

          const data = await response.json();

          if (data && data.user_info && data.user_info.screen_name && data.user_info.user_id) {
            logger.info("Successfully Verified the User", { username: data.user_info.screen_name });

            // Store user in localStorage for persistence
            localStorage.setItem('twitterUser', JSON.stringify(data.user_info));
            setTwitterUser(data.user_info);

            // Clean up sessionStorage
            sessionStorage.removeItem('oauth_state');

            // Redirect to home page (clean URL)
            window.location.href = '/';
          }
        } catch (error) {
          logger.error("Error fetching access token:", error);
          setAuthError('Error signing in with X. Please try again.');
          // Redirect to home after showing error
          setTimeout(() => {
            window.location.href = '/';
          }, 3000);
        }
      };

      getUserIdentity();
    }
  }, []);

  const fetchTwitterToken = async () => {
    logger.analytics('twitter-signin-initiated');
    try {
      const response = await fetch("/api/auth/twitter/request-token", {
        method: 'POST',
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle different error codes
        if (data.code === 'TWITTER_CREDENTIALS_MISSING') {
          setAuthError('Sign in with X is not configured on this server.');
        } else if (data.code === 'CALLBACK_URL_NOT_AUTHORIZED') {
          setAuthError('Sign in with X is only available on the official domain.');
        } else {
          setAuthError(data.error || 'Error initiating sign-in with X.');
        }
        logger.error("OAuth request failed:", data);
        return;
      }

      if (data && data.oauth_token) {
        setOauthToken(data.oauth_token);
        logger.info("Successfully set oauth token");

        // Store state token in sessionStorage if provided
        if (data.state) {
          sessionStorage.setItem('oauth_state', data.state);
          logger.info("Stored oauth state token");
        }

        // Redirect to Twitter for authorization
        window.location.href = `https://api.twitter.com/oauth/authorize?oauth_token=${data.oauth_token}`;
      } else {
        logger.error("Failed to get the OAuth token.");
        setAuthError('Error initiating sign-in with X. Please try again.');
      }
    } catch (error) {
      logger.error("Error fetching OAuth token:", error);
      setAuthError('Error connecting to X. Please try again.');
    }
  };

  const handleTwitterLogin = async () => {
    try {
      await fetchTwitterToken();

      if (oauthToken) {
        window.location.href = `https://api.twitter.com/oauth/authenticate?oauth_token=${oauthToken}`;
      }
    } catch (error) {
      logger.error("Error initiating Twitter OAuth flow:", error);
    }
  };

  const handleTwitterLogout = () => {
    // Clear user from state and localStorage
    setTwitterUser(null);
    localStorage.removeItem('twitterUser');
    logger.info('User logged out');
  };

  const HeaderWrapper = () => {
    const location = useLocation();
    const path = location.pathname;

    // Hide header during active gameplay
    const isChessGameplay = /^\/mferchess\/[^/]+$/.test(path) &&
                            !path.includes('/games') &&
                            !path.includes('/replay');
    const isMfersBeheadedGameplay = path.includes('/mfersbeheaded/level/') ||
                                     path === '/mfersbeheaded/pixelcrush' ||
                                     path === '/mfersbeheaded/tinydinos';
    const isAlien2048Gameplay = path === '/alien2048/play';

    if (isChessGameplay || isMfersBeheadedGameplay || isAlien2048Gameplay) {
      return null;
    }

    return <Header />;
  };

  const BugReportButtonWrapper = () => {
    const location = useLocation();

    // Allowlist of paths where bug report button should be displayed
    const allowedPaths = [
      /^\/mfersbeheaded($|\/)/,        // All mfersbeheaded routes
      /^\/mferchess\/replay\/.+/,      // Chess replay routes
      /^\/mferchess\/[^/]+$/,          // Chess gameplay routes (but not /games or landing)
      /^\/replay\/.+/                  // Mfersbeheaded replay routes
    ];

    // Check if current path matches any allowed pattern
    const isAllowedPath = allowedPaths.some(pattern => pattern.test(location.pathname));

    if (!isAllowedPath) {
      return null;
    }

    // Use portal to render button outside of #root to avoid overflow:hidden clipping
    return ReactDOM.createPortal(
      <BugReportButton />,
      document.body
    );
  };

  return (
    <WalletProvider>
      <AuthProvider>
        <ErrorBoundary level="app">
          <Router>
            <HeaderWrapper />

        {/* Auth Error Modal */}
        {authError && (
          <div className="modal-overlay" onClick={() => setAuthError(null)}>
            <div className="modal-content error-modal" onClick={(e) => e.stopPropagation()}>
              <h2>Sign In Error</h2>
              <p>{authError}</p>
              <button onClick={() => setAuthError(null)} className="modal-button">
                OK
              </button>
            </div>
          </div>
        )}

        <ErrorBoundary level="route">
          <Routes>
            <Route path="/" element={<LandingPageWithRefresh twitterUser={twitterUser} setTwitterUser={setTwitterUser} />} />
            <Route path="/auth/twitter/callback" element={<TwitterCallback />} />
            <Route path="/register" element={<RegistrationScreen />} />
            <Route path="/about" element={<About />} />
            <Route path="/settings" element={<ArcadeSettings />} />
            <Route path="/changelog" element={<Changelog />} />
            <Route path="/changelog/:id" element={<ChangelogDetail />} />
            <Route path="/mferchess/" element={<ChessLandingPage />} />
            <Route path="/mferchess/play" element={<ChessOffline />} />
            <Route path="/mferchess/games" element={<ChessGamesView />} />
            <Route path="/mferchess/replay/:gameId" element={<ChessReplay />} />
            <Route path="/mferchess/:gameId" element={
              <GameErrorBoundary gameName="MferChess" lobbyPath="/mferchess/">
                <GameChess />
              </GameErrorBoundary>
            } />
            <Route path="/larvalegends/" element={<LarvaLegendsLandingPage />} />
            <Route path="/larvalegends/:gameId" element={
              <GameErrorBoundary gameName="Larva Legends" lobbyPath="/larvalegends/">
                <LarvaLegends />
              </GameErrorBoundary>
            } />
            <Route path="/mfersbeheaded/" element={<MfersBeheadedNewLanding twitterUser={twitterUser} onTwitterSignIn={handleTwitterLogin} onTwitterSignOut={handleTwitterLogout} />} />
            <Route path="/mfersbeheaded/level/:levelId" element={
              <GameErrorBoundary gameName="Mfers Beheaded" lobbyPath="/mfersbeheaded/">
                <MfersBeheadedGame />
              </GameErrorBoundary>
            } />
            <Route path="/mfersbeheaded/pixelcrush" element={
              <GameErrorBoundary gameName="Mfers Beheaded" lobbyPath="/mfersbeheaded/">
                <MfersBeheadedGame />
              </GameErrorBoundary>
            } />
            <Route path="/mfersbeheaded/tinydinos" element={
              <GameErrorBoundary gameName="Mfers Beheaded" lobbyPath="/mfersbeheaded/">
                <MfersBeheadedGame />
              </GameErrorBoundary>
            } />
            <Route path="/mfersbeheaded/leaderboard" element={<Leaderboard />} />
            <Route path="/mfersbeheaded/settings" element={<MfersBeheadedSettingsPage twitterUser={twitterUser} onTwitterSignIn={handleTwitterLogin} onTwitterSignOut={handleTwitterLogout} />} />
            <Route path="/mferstetris/" element={<MfersTetrisLandingPage />} />
            <Route path="/mferstetris/play" element={
              <GameErrorBoundary gameName="Mfers Tetris" lobbyPath="/mferstetris/">
                <MfersTetris />
              </GameErrorBoundary>
            } />
            <Route path="/alien2048/" element={<Alien2048NewLanding twitterUser={twitterUser} onTwitterSignIn={handleTwitterLogin} onTwitterSignOut={handleTwitterLogout} />} />
            <Route path="/alien2048/settings" element={<Alien2048SettingsPage twitterUser={twitterUser} onTwitterSignIn={handleTwitterLogin} onTwitterSignOut={handleTwitterLogout} />} />
            <Route path="/alien2048/leaderboard" element={<Alien2048Leaderboard />} />
            <Route path="/alien2048/play" element={
              <GameErrorBoundary gameName="Alien 2048" lobbyPath="/alien2048/">
                <Alien2048 />
              </GameErrorBoundary>
            } />
            <Route path="/alien2048/replay/:sessionId" element={
              <ReplayViewer twitterUser={twitterUser} />
            } />
            <Route
              path="/mfersbeheaded/replay"
              element={
                <PrivateRoute twitterUser={twitterUser} requireTwitterUser="_nngmi">
                  <ReplayList twitterUser={twitterUser} />
                </PrivateRoute>
              }
            />
            <Route path="/mfersbeheaded/replay/:sessionId" element={<ReplayViewer twitterUser={twitterUser} />} />
          </Routes>
        </ErrorBoundary>

            {/* Bug Report Button - Only shown on allowed routes */}
            <BugReportButtonWrapper />
          </Router>
          <DebugConsole />
          <ScrollDebug />
        </ErrorBoundary>
      </AuthProvider>
    </WalletProvider>
  );
}

export default App;
